import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import styled from 'styled-components';
import {useDispatch, useSelector} from 'react-redux';

import {media} from '~/shared/theme/media';
import {MoneyCard, RevealMoneyCardResponse} from '~/shared/services/bffService/api/interfaces/responses';
import {usePersonalAreaContext} from '~/personalArea/context';
import TenbisCardsList from '~/personalArea/components/Sections/MyTenbis/TenbisCardsList';
import {selectUserData} from '~/shared/store/storeModules/user';
import {SimpleSpinnerLoader} from '~/shared/components/Loaders';
import {LoaderContainer} from '~/personalArea/components/Sections/Shared';
import {setCurrentModal} from '~/shared/store/storeModules/currentModal/currentModalActions';
import {trackEvent} from '~/shared/services/analytics';
import {revealMoneycard} from '~/shared/services/bffService/api';
import {createLogger} from '~/shared/logging';
import usePhoneVerification from '~/shared/hooks/usePhoneVerification';
import {ApiError, is401Error} from '~/shared/services/apiErrorService';
import {useIsMinLargeMobile} from '~/shared/hooks/deviceInfo';
import {getLocalizationService} from '~/shared/services/localisationService';
import {handleRefreshToken} from '~/shared/services/auth';

import TenbisCardView from './TenbisCardView';

const logger = createLogger('MyTenbis');

const Root = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px 16px;
  gap: 24px;
  ${media.minLargeTablet`
    padding: 40px 40px 20px;
  `}
`;

const MyTenbis = () => {
  const {t} = getLocalizationService();

  const timeoutRef = useRef<NodeJS.Timeout>();
  const [currentMoneycard, setCurrentMoneycard] = useState<MoneyCard>();
  const [cardDetails, setCardDetails] = useState<RevealMoneyCardResponse | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const {
    isLoading: isPaymentsRequestLoading,
    moneycards: allCards,
    activateMoneyCardById,
    getUserPayments,
    setMobileHeader,
  } = usePersonalAreaContext();
  const userData = useSelector(selectUserData);
  const dispatch = useDispatch();
  const {getUserCurrentSessionToken} = usePhoneVerification();
  const isMinLargeMobile = useIsMinLargeMobile();

  const verifyUser = useCallback((onSuccess: (id: string) => void) => {
    if (!userData?.email) {
      logger.warn('No email in user data! Cant verify the user');
      return;
    }
    if (!currentMoneycard) {
      logger.warn('No moneycard to get details for');
      return;
    }
    dispatch(setCurrentModal('phoneVerificationModal', {
      email: userData.email,
      onSuccess: () => onSuccess(currentMoneycard.id),
    }));
  }, [userData, currentMoneycard, dispatch]);

  const getMoneyCardDetails = useCallback(async (id: string) => {
    try {
      const data = await revealMoneycard(id);
      setCardDetails(data);
    } catch (err) {
      if (is401Error(err)) {
        await handleRefreshToken(err, getMoneyCardDetails, id);
        return;
      }

      if (err instanceof ApiError && err.status === 428) {
        verifyUser(getMoneyCardDetails);
        return;
      }
      logger.warn('Something went wrong');
      throw err;
    }
  }, [verifyUser]);

  const onShowCardDetailsClick = useCallback(async () => {
    if (!currentMoneycard) {
      logger.warn('No moneycard to get details for');
      return;
    }
    
    if (!getUserCurrentSessionToken()) {
      verifyUser(getMoneyCardDetails);
      return;
    }

    await getMoneyCardDetails(currentMoneycard.id);
  }, [currentMoneycard, verifyUser, getMoneyCardDetails, getUserCurrentSessionToken]);

  const hideDetails = useCallback(() => {
    setCardDetails(undefined);
  }, []);

  const onBackButtonClick = useCallback(() => {
    setCurrentMoneycard(undefined);
    setMobileHeader(null);
  }, [setMobileHeader]);

  const onMoneycardClick = useCallback((moneycard: MoneyCard) => {
    setMobileHeader({
      title: t('tenbis_card_xxx', {fourDigits: moneycard.cardLastDigits}),
      onBackClick: onBackButtonClick,
    });
    setCurrentMoneycard(moneycard);
  }, [onBackButtonClick, setMobileHeader, t]);

  const refetchPayments = useCallback(async () => {
    try {
      await getUserPayments();
      setCurrentMoneycard(undefined);
    } catch (error) {
      if (is401Error(error)) {
        await handleRefreshToken(error, refetchPayments);
      }
    }
  }, [getUserPayments]);

  const onActivateMoneycardClick = useCallback((id: string) => {
    trackEvent('mcActivate_click');
    dispatch(setCurrentModal('pieActivateMoneycardModal', {
      onSuccess: async () => {
        setCurrentMoneycard(mc => (mc && mc.id === id ? {...mc, isActivated: true} : mc));
        activateMoneyCardById(id);
        await refetchPayments();
      },
    }));
  }, [dispatch, activateMoneyCardById, refetchPayments]);

  const moneycards = useMemo(() => allCards?.filter(moneycard => !moneycard.isTenbisCredit) || [], [allCards]);

  useEffect(() => {
    if (!isPaymentsRequestLoading) {
      if (!currentMoneycard && moneycards?.length === 1) {
        setCurrentMoneycard(moneycards[0]);
      }
      setIsLoading(false);
    }
    if (isPaymentsRequestLoading) {
      setIsLoading(true);
    }
  }, [isPaymentsRequestLoading, moneycards, currentMoneycard]);

  useEffect(() => {
    if (cardDetails && cardDetails.cVV) {
      timeoutRef.current = setTimeout(() => {
        hideDetails();
      }, 60000);
    }

    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, [hideDetails, cardDetails]);

  if (isLoading) {
    return (
      <LoaderContainer>
        <SimpleSpinnerLoader variant="pieBig" />
      </LoaderContainer>
    );
  }

  return (
    <Root>
      {currentMoneycard && (
        <TenbisCardView
          currentMoneycard={currentMoneycard}
          cardDetails={cardDetails}
          refetchPayments={refetchPayments}
          onBackButtonClick={moneycards.length > 1 && isMinLargeMobile ? onBackButtonClick : null}
          userData={userData}
          onActivateMoneycardClick={onActivateMoneycardClick}
          onShowDetailsClick={onShowCardDetailsClick}
          onHideDetailsClick={hideDetails}
        />
      )}
      {!currentMoneycard && !!moneycards && !!moneycards.length && (
        <TenbisCardsList
          moneycards={moneycards}
          onMoneycardClick={onMoneycardClick}
          onActivateMoneycardClick={onActivateMoneycardClick}
          isMinLargeMobile={isMinLargeMobile}
        />
      )}
    </Root>
  );
};

export default MyTenbis;
